import { useState, useEffect } from "react";
import { Save, AlertCircle, CheckCircle2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { translations, Lang } from "@/i18n/translations";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface StatItem {
  value: string;
  label: string;
}

type LocalStatsState = Record<Lang, StatItem[]>;

const AdminHeroStatsPage = () => {
  const { toast } = useToast();
  
  // Initialize with values from translations or localstorage
  const [stats, setStats] = useState<LocalStatsState>(() => {
    try {
      const savedDraft = localStorage.getItem("kaynin_hero_stats_draft");
      if (savedDraft) {
        return JSON.parse(savedDraft);
      }
      const savedLive = localStorage.getItem("kaynin_hero_stats");
      if (savedLive) {
        return JSON.parse(savedLive);
      }
    } catch (e) {
      console.error("Failed to parse saved hero stats");
    }
    
    // Default fallback
    return {
      fr: JSON.parse(JSON.stringify(translations.fr.hero.stats)),
      ar: JSON.parse(JSON.stringify(translations.ar.hero.stats)),
      zgh: JSON.parse(JSON.stringify(translations.zgh.hero.stats)),
    };
  });

  const [activeLang, setActiveLang] = useState<Lang>("fr");
  const [isDirty, setIsDirty] = useState(false);

  // Handle stat change
  const handleChange = (index: number, field: "value" | "label", value: string) => {
    const newStats = { ...stats };
    newStats[activeLang][index][field] = value;
    setStats(newStats);
    setIsDirty(true);
  };

  // Reset to default translations
  const handleReset = () => {
    if (confirm("Voulez-vous vraiment réinitialiser toutes les statistiques à leurs valeurs d'origine ?")) {
      setStats({
        fr: JSON.parse(JSON.stringify(translations.fr.hero.stats)),
        ar: JSON.parse(JSON.stringify(translations.ar.hero.stats)),
        zgh: JSON.parse(JSON.stringify(translations.zgh.hero.stats)),
      });
      setIsDirty(true);
    }
  };

  // Save as draft
  const handleSaveDraft = () => {
    try {
      localStorage.setItem("kaynin_hero_stats_draft", JSON.stringify(stats));
      window.dispatchEvent(new Event("kayninHeroStatsDraftUpdated"));
      
      toast({
        title: "Brouillon enregistré",
        description: "Vos modifications sont sauvegardées mais ne sont pas encore visibles par le public.",
        variant: "default",
      });
      setIsDirty(false);
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer le brouillon.",
        variant: "destructive",
      });
    }
  };

  // Save to local storage and publish
  const handlePublish = () => {
    try {
      localStorage.setItem("kaynin_hero_stats", JSON.stringify(stats));
      localStorage.setItem("kaynin_hero_stats_draft", JSON.stringify(stats));
      window.dispatchEvent(new Event("kayninHeroStatsUpdated"));
      
      toast({
        title: "Statistiques publiées en ligne",
        description: "Les nouveaux chiffres sont désormais visibles sur la page d'accueil public.",
        variant: "default",
      });
      setIsDirty(false);
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible de publier les statistiques.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground">Gestion des Statistiques (Stat Navbar)</h1>
        <p className="text-muted-foreground mt-2">
          Modifiez ici les chiffres clés affichés sur la page d'accueil (ex: Électeurs inscrits, Quota femmes...).<br/>
          Les modifications sont instantanément répercutées sur le site public après sauvegarde.
        </p>
      </div>

      {isDirty && (
        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription>
            Vous avez des modifications non enregistrées. N'oubliez pas de les publier.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Chiffres clés par langue</CardTitle>
          <CardDescription>Configurez les valeurs et étiquettes pour chaque langue du site.</CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeLang} onValueChange={(val) => setActiveLang(val as Lang)} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="fr" className="font-body">Français</TabsTrigger>
              <TabsTrigger value="ar" className="font-arabic font-bold text-lg">العربية</TabsTrigger>
              <TabsTrigger value="zgh" className="font-amazigh font-bold text-lg">ⵜⴰⵎⴰⵣⵉⵖⵜ</TabsTrigger>
            </TabsList>
            
            {(["fr", "ar", "zgh"] as Lang[]).map((lang) => (
              <TabsContent key={lang} value={lang} className="space-y-6">
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${lang === 'ar' ? 'h-[dir=rtl]' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  {stats[lang].map((stat, index) => (
                    <div key={index} className="p-4 rounded-xl border border-border bg-muted/20 space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-primary">Statistique n°{index + 1}</span>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Valeur brute (ex: 17M+)</Label>
                        <Input 
                          value={stat.value}
                          onChange={(e) => handleChange(index, "value", e.target.value)}
                          className={`font-bold text-lg ${lang === 'ar' ? 'font-arabic' : lang === 'zgh' ? 'font-amazigh' : 'font-body'}`}
                          dir="auto"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Étiquette (ex: Électeurs inscrits)</Label>
                        <Input 
                          value={stat.label}
                          onChange={(e) => handleChange(index, "label", e.target.value)}
                          className={lang === 'ar' ? 'font-arabic' : lang === 'zgh' ? 'font-amazigh' : 'font-body'}
                          dir="auto"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>

        <CardFooter className="flex justify-between border-t border-border pt-6 bg-muted/10 gap-3 flex-wrap">
          <Button variant="ghost" onClick={handleReset} className="text-muted-foreground hover:text-destructive gap-2 text-sm">
            <RotateCcw className="w-4 h-4" /> Réinitialiser
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSaveDraft} className="gap-2 font-semibold min-w-[150px]" disabled={!isDirty}>
              <Save className="w-4 h-4" /> Enregistrer un brouillon
            </Button>
            <Button onClick={handlePublish} className="gap-2 font-semibold min-w-[200px] bg-green-600 hover:bg-green-700 text-white shadow-md">
              <CheckCircle2 className="w-4 h-4" /> Publier au Grand Public
            </Button>
          </div>
        </CardFooter>
      </Card>
      
      {/* Preview */}
      <h3 className="font-display font-semibold text-lg mt-8 mb-4">Aperçu du rendu (en ligne):</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-8 rounded-xl bg-slate-900 border border-slate-800" dir={activeLang === 'ar' ? 'rtl' : 'ltr'}>
        {stats[activeLang].map((stat, idx) => (
          <div key={idx} className="text-center p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
            <div className={`text-2xl md:text-3xl font-display font-bold text-accent ${activeLang === 'ar' ? 'font-arabic' : activeLang === 'zgh' ? 'font-amazigh' : ''}`}>
              {stat.value}
            </div>
            <div className={`text-sm text-white/70 mt-1 ${activeLang === 'ar' ? 'font-arabic' : activeLang === 'zgh' ? 'font-amazigh' : 'font-body'}`}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminHeroStatsPage;
